/* Wild Shape's Beast forms (Druid 2, D&D 2024 / SRD 5.2.1). A form is a snapshot of the Beast's stat block saved at
 * `classState.druid.wildShape.form`, so the sheet, the DM's records and every server read see the same numbers.
 * CommonJS like divineOrder.js: the ability score functions that read it are synchronous on both runtimes. It avoids spread,
 * rest and array destructuring: Babel's helpers for those are imports, and Webpack then can't see `module.exports`.
 * Equipment merges into the form, so its effects stop. Pack Tactics, Bloodied Fury, Agile, Flyby, Swarm and Prone on a hit
 * are automated; every other trait is text for the table.
 */
const ROUNDS_PER_HOUR = 600;
const BEAST_SHAPES = Object.freeze([
  Object.freeze({ level: 2, forms: 4, challengeRating: 0.25, fly: false }),
  Object.freeze({ level: 4, forms: 6, challengeRating: 0.5, fly: false }),
  Object.freeze({ level: 8, forms: 8, challengeRating: 1, fly: true }),
]);
const SCORE_KEYS = Object.freeze({ str: 'strength', dex: 'dexterity', con: 'constitution' });
const COUNT_WORDS = Object.freeze({ one: 1, two: 2, three: 3, four: 4 });
const SIZES = Object.freeze(['tiny', 'small', 'medium', 'large', 'huge', 'gargantuan']);

/** The Beast Shapes row for this Druid level, or null below 2. */
const beastShapeLimit = (level) => BEAST_SHAPES.filter((row) => Number(level) >= row.level).pop() || null;
const feet = (value) => Number.parseInt(String(value ?? ''), 10) || 0;
const speedsOf = (block) => Object.keys(block?.speed || {}).reduce((speeds, mode) => {
  const value = feet(block.speed[mode]);
  if (value > 0) speeds[mode] = value;
  return speeds;
}, {});
const isBeast = (block) => /\bbeasts?\b/i.test(String(block?.type || ''));
/** Whether this stat block can be learned at this row: a Beast within the Challenge Rating, with a Fly Speed only from Druid 8. */
const isEligibleBeast = (block, limit) => Boolean(limit) && isBeast(block) && Number(block.challenge_rating) <= limit.challengeRating && (limit.fly || !speedsOf(block).fly);

/** The largest size a hit knocks Prone by itself, or null. A rider that also needs a charge stays in the text. */
/** The largest size an attack knocks Prone on a hit ('medium'), read from its text; a charge that needs a run-up doesn't count. */
const actionProneLimit = (action) => proneLimitOf(String((action && action.desc) || ''));
const proneLimitOf = (desc) => (/moved \d+\+? feet/i.test(desc) ? null : /If the target is a (\w+) or smaller creature, it has the Prone condition/i.exec(desc)?.[1]?.toLowerCase() || null);
/** Whether a creature of this size is at or under the limit. An unknown size counts as Medium. */
const sizeWithin = (size, limit) => SIZES.indexOf(String(size || 'medium').toLowerCase()) <= SIZES.indexOf(limit);

/** The attacks a stat block gives, with their numbers as printed. Anything without an attack bonus stays in the text. */
const beastAttacks = (block) => (block?.actions || [])
  .filter((action) => Number.isFinite(action?.attack_bonus) && (action.damage || []).length)
  .map((action) => ({
    name: action.name, attackBonus: action.attack_bonus, ranged: /^ranged/i.test(String(action.desc || '')),
    reachFeet: Number(/reach (\d+) ft/i.exec(String(action.desc || ''))?.[1]) || 5,
    damage: action.damage.map((part) => ({ dice: String(part.damage_dice).replace(/\s+/g, ''), type: String(part.damage_type?.name ?? part.damage_type ?? '').toLowerCase() })),
    desc: String(action.desc || ''), proneLimit: proneLimitOf(String(action.desc || '')),
  }));
/** How many attacks a Multiattack makes: "two Rend attacks", "one Bite attack and one Claw attack". */
const multiattackCount = (block) => {
  const action = (block?.actions || []).find((entry) => /^multiattack/i.test(String(entry?.name || '')));
  const words = String(action?.desc || '').toLowerCase().match(/\b(?:one|two|three|four)\b/g) || [];
  return Math.max(1, words.reduce((sum, word) => sum + COUNT_WORDS[word], 0));
};
const traitsOf = (block) => [].concat(block?.special_abilities || [], (block?.actions || []).filter((action) => !Number.isFinite(action?.attack_bonus) && !/^multiattack/i.test(String(action?.name || ''))),
  block?.bonus_actions || [], block?.reactions || []).map(({ name, desc }) => ({ name, desc: String(desc || '') }));

/** What the form list shows. */
const beastSummary = (block) => ({
  index: block.index, name: block.name, size: block.size, challengeRating: Number(block.challenge_rating), armorClass: block.armor_class, speed: speedsOf(block),
  attacks: beastAttacks(block).map(({ name, attackBonus, damage }) => ({ name, attackBonus, damage: damage.map((part) => `${part.dice} ${part.type}`.trim()).join(' + ') })),
  attackCount: multiattackCount(block), traits: traitsOf(block).map(({ name }) => name),
});

/** The form saved on the character. `beastSpells` is Druid 18; `figurine` is the Beast's token art, if the app has any. */
const beastFormSnapshot = (block, { druidLevel, beastSpells = false, figurine = null }) => {
  const form = {
    index: block.index, name: block.name, size: block.size, armorClass: block.armor_class, speed: speedsOf(block),
    scores: Object.keys(SCORE_KEYS).reduce((scores, key) => { scores[key] = Number(block[SCORE_KEYS[key]]) || 10; return scores; }, {}),
    skills: block.skills || {}, attacks: beastAttacks(block), attackCount: multiattackCount(block), traits: traitsOf(block),
    roundsLeft: Math.max(1, Math.floor(Number(druidLevel) / 2)) * ROUNDS_PER_HOUR, beastSpells,
  };
  if (figurine) form.figurine = figurine;
  return form;
};

const activeBeastForm = (character) => character?.classState?.druid?.wildShape?.form || null;
/** The character without the form: Hit Points, which keep the Druid's own Constitution, read this. */
const withoutBeastForm = (character) => {
  if (!activeBeastForm(character)) return character;
  const wildShape = Object.assign({}, character.classState.druid.wildShape);
  delete wildShape.form;
  return Object.assign({}, character, { classState: Object.assign({}, character.classState, { druid: Object.assign({}, character.classState.druid, { wildShape }) }) });
};
/** The form's Strength, Dexterity or Constitution, or null when it doesn't replace this score. */
const beastFormScore = (character, ability) => activeBeastForm(character)?.scores?.[ability] ?? null;
/** A creature's traits: a Druid's Beast form, or an enemy's own stat block (a Wolf's Pack Tactics, a Boar's Bloodied Fury). */
const creatureTraits = (creature) => {
  const form = activeBeastForm(creature);
  if (form) return form.traits || [];
  const block = creature && (creature.specialAbilities || creature.special_abilities);
  return Array.isArray(block) ? block : [];
};
const hasBeastTrait = (character, name) => creatureTraits(character).some((trait) => Boolean(trait) && trait.name === name);
/** Agile and Flyby: leaving an enemy's reach provokes no Opportunity Attack. */
const avoidsOpportunityAttacks = (character) => hasBeastTrait(character, 'Agile') || hasBeastTrait(character, 'Flyby');
/** The Temporary Hit Points a form gives. A Swarm can't gain any. */
const beastFormTemporaryHitPoints = (form, level) => (form?.traits?.some((trait) => trait.name === 'Swarm') ? 0 : level);
/** A skill bonus in the stat block replaces a lower one of the character's own. */
const withBeastFormSkill = (character, skill, bonus) => {
  const listed = Number(activeBeastForm(character)?.skills?.[skill]);
  return Number.isFinite(listed) ? Math.max(bonus, listed) : bonus;
};
/** The time left after `rounds` pass, or null when the form runs out. */
const beastFormAfterTime = (form, rounds) => {
  const roundsLeft = Number(form?.roundsLeft) - rounds;
  return roundsLeft > 0 ? Object.assign({}, form, { roundsLeft }) : null;
};
/** Whole hours left, rounded up, for the tile. */
const beastFormHoursLeft = (form) => Math.ceil((Number(form?.roundsLeft) || 0) / ROUNDS_PER_HOUR);

const shiftFlat = (dice, change) => {
  const parts = /^(\d+)(?:d(\d+))?([+-]\d+)?$/.exec(dice);
  if (!parts) return dice;
  if (!parts[2]) return String(Math.max(0, Number(parts[1]) + change));
  const total = (Number(parts[3]) || 0) + change;
  return `${parts[1]}d${parts[2]}${total ? `${total > 0 ? '+' : ''}${total}` : ''}`;
};
/**
 * The attack as a sheet weapon. The attack flow adds the Proficiency Bonus and the ability modifier, so the numbers are
 * shifted to land on what the stat block prints: its own bonus and damage, not the Druid's Proficiency Bonus.
 */
const beastWeapon = (attack, { proficiencyBonus, strengthModifier, dexterityModifier }) => {
  const ability = attack.ranged ? dexterityModifier : strengthModifier;
  return {
    name: attack.name, type: 'Natural', category: attack.ranged ? 'Ranged Weapon' : 'Melee Weapon', source: 'weapon', beastAttack: true, proneLimit: attack.proneLimit || null,
    properties: attack.reachFeet > 5 ? ['Reach'] : [], attackBonus: attack.attackBonus - proficiencyBonus - ability,
    damage: attack.damage.map((part, index) => `${index ? part.dice : shiftFlat(part.dice, -ability)} ${part.type}`.trim()).join(' + '),
  };
};

/**
 * Why these known forms can't be saved, or ''. `forms` are Beast indexes; `catalog` looks one up. A form can be dropped
 * only with `canSwap` (one after each Long Rest), and new room from a level fills without counting against that.
 */
const knownFormsProblem = ({ level, saved = [], forms, catalog, canSwap }) => {
  const limit = beastShapeLimit(level);
  if (!limit) return 'Wild Shape needs Druid level 2.';
  if (!Array.isArray(forms) || new Set(forms).size !== forms.length || !forms.every((index) => typeof index === 'string')) return 'Choose different Beast forms.';
  if (forms.length !== limit.forms) return `Choose ${limit.forms} forms.`;
  if (!forms.every((index) => isEligibleBeast(catalog(index), limit))) return 'One of those Beasts is beyond your Druid level.';
  const dropped = saved.filter((index) => !forms.includes(index)).length;
  if (dropped > (canSwap ? 1 : 0)) return canSwap ? 'You can replace one form after a Long Rest.' : 'You can replace a form after your next Long Rest.';
  return '';
};

/** The saved known forms, as Beast indexes. */
const getKnownForms = (character) => { const forms = character?.classState?.druid?.knownForms?.forms; return Array.isArray(forms) ? forms : []; };
/** A level gave room for forms the Druid hasn't chosen yet. */
const needsKnownForms = (character, level) => { const limit = beastShapeLimit(level); return Boolean(limit) && getKnownForms(character).length < limit.forms; };
/** One form can be replaced after each Long Rest. */
const canSwapKnownForm = (character) => (Number(character?.restState?.longCount) || 0) > (Number(character?.classState?.druid?.knownForms?.changedAtLongRest) || 0);

module.exports = { BEAST_SHAPES, sizeWithin, actionProneLimit, hasBeastTrait, avoidsOpportunityAttacks, beastFormTemporaryHitPoints, getKnownForms, needsKnownForms, canSwapKnownForm, beastShapeLimit, isEligibleBeast, beastAttacks, multiattackCount, beastSummary, beastFormSnapshot, activeBeastForm, withoutBeastForm, beastFormScore, withBeastFormSkill,
  beastFormAfterTime, beastFormHoursLeft, beastWeapon, knownFormsProblem };
